import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  loadProviderAvailableAppointments,
  PracticeOnly,
  ProviderAndPractice,
  SchedulingConfig,
} from './common';
import AppointmentModal from './AppointmentModal';
import Appointments from './Appointments';

import * as api from '../../../api';
import * as rootReducer from '../../../reducers/root';

import styles from '../index.module.css';

interface Props {
  timeSlots: api.EhrTimeSlot.EhrTimeSlot[];
  config: ProviderAndPractice | PracticeOnly;
  onAppointmentSelected: (appointment: api.EhrTimeSlot.EhrTimeSlot) => void;
  fieldConfig: SchedulingConfig;
  patientOrder?: api.EhrTimeSlot.EhrTimeSlotPatientOrder;
}

const AppointmentView: React.FC<Props> = ({
  timeSlots,
  config,
  onAppointmentSelected,
  fieldConfig,
  patientOrder,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [providerEhrTimeSlots, setProviderEhrTimeSlots] = useState<
    api.EhrTimeSlot.EhrTimeSlot[] | null
  >(null);

  const setOnCaptchaSuccess = useCallback(
    (onCaptchaSuccess: (() => void) | null) => {
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess));
    },
    [dispatch],
  );

  const viewMoreAvailability = (
    <div
      className={styles.SeeAllAppointmentButton}
      onClick={async () => {
        const timeSlots = await loadProviderAvailableAppointments({
          practiceId: config.practiceId,
          formFieldId: fieldConfig.previsitFormFieldId,
          providerId:
            'providerUserId' in config ? config.providerUserId : undefined,
          setOnCaptchaSuccess,
        });
        setIsOpen(true);
        setProviderEhrTimeSlots(timeSlots);
      }}
    >
      {t('View more availability')}
    </div>
  );

  return (
    <>
      <Appointments
        timeSlots={timeSlots}
        providerId={
          'providerUserId' in config ? config.providerUserId : undefined
        }
        practiceId={config.practiceId}
        timezone={config.timezone}
        onAppointmentSelected={onAppointmentSelected}
        viewMoreAvailability={patientOrder && viewMoreAvailability}
        hasAlternateProvider={!!patientOrder?.alternateReferredToProvider}
      />
      {!patientOrder && viewMoreAvailability}
      {isOpen ? (
        <AppointmentModal
          onAppointmentSelected={onAppointmentSelected}
          providerEhrTimeSlots={providerEhrTimeSlots}
          config={config}
          fieldConfig={fieldConfig}
          isOpen={isOpen}
          onModalClose={() => {
            setIsOpen(false);
            setProviderEhrTimeSlots(null);
          }}
          id={
            'providerUserId' in config &&
            fieldConfig.scheduleAppointmentDisplayBy ===
              api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
                .Provider
              ? config.providerUserId
              : config.practiceId
          }
        />
      ) : null}
    </>
  );
};

export default AppointmentView;
